import React, { useState, useRef, useEffect } from 'react';
import QrScanner from 'qr-scanner';
import { validateSKReceiptQR, getValidationErrorMessage } from '../utils/qrValidation';

interface QRScannerViewProps {
  onScanSuccess: (uid: string, format: 'online' | 'offline') => void;
  onError: (message: string) => void;
  onPhotoUpload: () => void;
  onManualInput: () => void;
  onNonSkReceipt: () => void;
  moreBills: string;
  onMoreBillsChange: (value: string) => void;
  statusMessage?: string;
  isProcessing?: boolean;
  processingSuccess?: boolean;
}

const QRScannerView: React.FC<QRScannerViewProps> = ({
  onScanSuccess,
  onError,
  onPhotoUpload,
  onManualInput,
  onNonSkReceipt,
  moreBills,
  onMoreBillsChange,
  statusMessage,
  isProcessing,
  processingSuccess
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [isScanning, setIsScanning] = useState(true);
  const [lastScanResult, setLastScanResult] = useState<string>('');
  const [debugInfo, setDebugInfo] = useState<string>(''); // Visual debugging

  useEffect(() => {
    if (!videoRef.current) return;

    console.log('[QRScanner] Initializing scanner...');

    const qrScanner = new QrScanner(
      videoRef.current,
      (result: any) => {
        setDebugInfo(`Callback fired! Type: ${typeof result}`);
        console.log('[QRScanner] Raw scan result:', result);
        console.log('[QRScanner] Result type:', typeof result);
        if (result && typeof result === 'object') {
          console.log('[QRScanner] Result keys:', Object.keys(result));
          setDebugInfo(`Keys: ${Object.keys(result).join(', ')}`);
        }

        // Handle both string and object formats
        let qrData = '';
        if (typeof result === 'string') {
          qrData = result;
          setDebugInfo(`String: ${result.substring(0, 20)}...`);
        } else if (result && typeof result === 'object') {
          // Try multiple possible properties
          qrData = result.data || result.rawValue || result.text || '';
          setDebugInfo(`Extracted: ${qrData.substring(0, 20)}...`);
        }

        console.log('[QRScanner] Extracted QR data:', qrData);

        if (qrData && qrData.trim()) {
          setDebugInfo(`Calling handleScan...`);
          handleScan(qrData.trim());
        } else {
          console.error('[QRScanner] No valid data in result:', result);
          setDebugInfo(`ERROR: No data extracted!`);
        }
      },
      {
        onDecodeError: (error: any) => {
          // This fires constantly while scanning, so only log real errors
          if (error && error.message && !error.message.includes('No QR code found')) {
            console.warn('[QRScanner] Decode error:', error.message);
          }
        },
        preferredCamera: 'environment',
        highlightScanRegion: false,
        highlightCodeOutline: false,
        maxScansPerSecond: 5
      }
    );

    scannerRef.current = qrScanner;

    // Check flash capability
    qrScanner.hasFlash().then((hasFlashSupport) => {
      console.log('[QRScanner] Flash available:', hasFlashSupport);
      setHasFlash(hasFlashSupport);
    }).catch(() => {
      setHasFlash(false);
    });

    // Start scanner
    qrScanner.start().then(() => {
      console.log('[QRScanner] Scanner started successfully');
    }).catch((err) => {
      console.error('[QRScanner] Failed to start scanner:', err);
      onError('Nepodarilo sa spustiť kameru. Skontrolujte povolenia.');
    });

    // Cleanup on unmount
    return () => {
      console.log('[QRScanner] Cleaning up scanner...');
      qrScanner.stop();
      qrScanner.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restart scanner after SUCCESSFUL processing (for "more bills" mode)
  // Only restart on success, not on errors
  useEffect(() => {
    if (!isProcessing && processingSuccess && lastScanResult && scannerRef.current && moreBills === 'yes') {
      // Processing succeeded, restart scanner for next bill
      console.log('[QRScanner] Processing succeeded, restarting scanner for next bill');

      // Small delay to prevent race conditions
      const timer = setTimeout(() => {
        setLastScanResult(''); // Clear to allow scanning the same receipt again
        setDebugInfo('');
        setIsScanning(true);

        scannerRef.current?.start().catch((err) => {
          console.error('[QRScanner] Failed to restart scanner:', err);
        });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isProcessing, processingSuccess, moreBills, lastScanResult]);

  const handleScan = (qrData: string) => {
    try {
      console.log('[QRScanner] handleScan called with:', qrData);
      setDebugInfo(`handleScan: ${qrData.substring(0, 30)}...`);

      // Prevent duplicate scans
      if (qrData === lastScanResult) {
        console.log('[QRScanner] Duplicate scan, ignoring');
        setDebugInfo(`Duplicate, ignoring`);
        return;
      }
      setLastScanResult(qrData);

      const validation = validateSKReceiptQR(qrData);
      console.log('[QRScanner] Validation result:', validation);
      setDebugInfo(`Valid: ${validation.valid}, Format: ${validation.format}`);

      if (validation.valid) {
        console.log('[QRScanner] Valid QR code detected!');
        setDebugInfo(`✅ Valid! Stopping scanner...`);

        // STOP SCANNER IMMEDIATELY to prevent re-scanning during API calls
        if (scannerRef.current) {
          console.log('[QRScanner] Stopping scanner to prevent re-scans');
          scannerRef.current.stop();
        }

        // Play success feedback
        playSuccessFeedback();

        // Pause scanning
        setIsScanning(false);

        // Call success callback
        console.log('[QRScanner] Calling onScanSuccess with:', validation.uid, validation.format);
        onScanSuccess(validation.uid, validation.format!);
        console.log('[QRScanner] onScanSuccess called successfully');
        setDebugInfo(`✅ Processing...`);
      } else {
        console.log('[QRScanner] Invalid QR code format');

        // Show validation error - user-friendly message
        const errorMsg = getValidationErrorMessage(qrData);
        setDebugInfo(`❌ ${errorMsg}`);
        onError(errorMsg);

        // Clear last result to allow retry
        setTimeout(() => {
          setLastScanResult('');
          setDebugInfo('');
        }, 2000);
      }
    } catch (error: any) {
      console.error('[QRScanner] Error in handleScan:', error);
      setDebugInfo(`❌ ERROR: ${error.message}`);
      onError(`Scanner error: ${error.message || 'Unknown error'}`);
      setIsScanning(true); // Resume scanning on error
      setTimeout(() => {
        setLastScanResult('');
        setDebugInfo('');
      }, 2000);
    }
  };

  const playSuccessFeedback = () => {
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }

    // Audio feedback (optional - can add beep.mp3 to public folder)
    try {
      const audio = new Audio('/beep.mp3');
      audio.play().catch(() => {
        // Ignore if audio fails
      });
    } catch {
      // Audio not available
    }
  };

  const toggleFlash = async () => {
    if (scannerRef.current && hasFlash) {
      try {
        await scannerRef.current.toggleFlash();
        setFlashOn(!flashOn);
      } catch (err) {
        console.error('Failed to toggle flash:', err);
      }
    }
  };

  const actionButtonStyle: React.CSSProperties = {
    background: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    padding: '14px 20px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#374151',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
    transition: 'all 0.2s',
    width: '100%',
  };

  const flashButtonStyle: React.CSSProperties = {
    background: flashOn ? '#667eea' : '#f3f4f6',
    color: flashOn ? 'white' : '#374151',
    border: 'none',
    borderRadius: '24px',
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: '0 20px'
    }}>
      {/* Instructions */}
      <div style={{
        textAlign: 'center',
        padding: '16px 0',
        color: isProcessing ? '#667eea' : '#6b7280',
        fontSize: '14px',
        fontWeight: isProcessing ? 600 : 400
      }}>
        {isProcessing && statusMessage ? statusMessage : (isScanning ? '📱 Namier kameru na QR kód' : '✅ Skenujem...')}
      </div>

      {/* Error/info messages - only show errors */}
      {debugInfo && debugInfo.includes('❌') && (
        <div style={{
          background: '#fef3c7',
          border: '2px solid #f59e0b',
          borderRadius: '8px',
          padding: '12px',
          margin: '0 0 12px 0',
          fontSize: '14px',
          wordBreak: 'break-word',
          color: '#92400e',
          textAlign: 'center'
        }}>
          {debugInfo}
        </div>
      )}

      {/* Video element - hide during processing */}
      {!isProcessing && (
        <video
          ref={videoRef}
          style={{
            width: '100%',
            maxHeight: '50vh',
            objectFit: 'cover',
            borderRadius: '12px',
            marginBottom: '16px',
            background: '#000'
          }}
        />
      )}

      {/* Processing indicator */}
      {isProcessing && (
        <div style={{
          width: '100%',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          marginBottom: '16px',
          fontSize: '48px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>⏳</div>
            <div style={{ fontSize: '16px', color: 'white', fontWeight: 600 }}>
              {statusMessage || 'Spracovávam...'}
            </div>
          </div>
        </div>
      )}

      {/* Flash toggle - centered, only if available */}
      {hasFlash && (
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <button
            onClick={toggleFlash}
            style={flashButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {flashOn ? '💡 Zhasnúť' : '🔦 Svietiť'}
          </button>
        </div>
      )}

      {/* More Bills Toggle - positioned after scanner */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '10px 12px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px'
          }}>
            <span style={{
              fontSize: '13px',
              color: '#374151',
              fontWeight: 500
            }}>
              Budeš nahrávať viac bločkov naraz?
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <div
                style={{
                  position: 'relative',
                  width: '56px',
                  height: '32px',
                  background: moreBills === 'yes' ? '#667eea' : '#d1d5db',
                  borderRadius: '16px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  transition: 'background .25s ease',
                  outline: 'none',
                  opacity: isProcessing ? 0.5 : 1
                }}
                onClick={() => !isProcessing && onMoreBillsChange(moreBills === 'no' ? 'yes' : 'no')}
              >
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: moreBills === 'yes' ? '26px' : '2px',
                  width: '28px',
                  height: '28px',
                  background: '#fff',
                  borderRadius: '50%',
                  boxShadow: '0 2px 4px rgba(0,0,0,.15)',
                  transition: 'left .25s ease'
                }} />
              </div>
              <span style={{
                fontSize: '13px',
                color: moreBills === 'yes' ? '#667eea' : '#9ca3af',
                fontWeight: moreBills === 'yes' ? 700 : 500,
                transition: 'color .2s, font-weight .2s',
                whiteSpace: 'nowrap'
              }}>
                {moreBills === 'yes' ? 'Áno' : 'Nie'}
              </span>
            </div>
          </div>
        </div>
        <div style={{
          fontSize: '11px',
          color: '#6b7280',
          marginTop: '6px',
          textAlign: 'center',
          lineHeight: '1.3',
          padding: '0 4px'
        }}>
          {moreBills === 'yes'
            ? '✅ Rýchlejšie nascanuješ a uložíš viac bločkov. Kategorizácia sa spustí neskôr a spracuje všetky položky.'
            : 'ℹ️ Hneď po nahratí bločka sa spustí kategorizácia položiek'}
        </div>
      </div>

      {/* Quick action buttons - hide during processing */}
      {!isProcessing && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginTop: '8px'
        }}>
          {/* Upload from gallery - for screenshots */}
          <button
            onClick={onPhotoUpload}
            style={actionButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#667eea';
            e.currentTarget.style.background = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.background = 'white';
          }}
        >
          <span style={{ fontSize: '20px' }}>📸</span>
          <span>Nahrať z telefónu</span>
        </button>

        {/* Manual UID input */}
        <button
          onClick={onManualInput}
          style={actionButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#667eea';
            e.currentTarget.style.background = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.background = 'white';
          }}
        >
          <span style={{ fontSize: '20px' }}>✍️</span>
          <span>Zadať UID manuálne</span>
        </button>

        {/* Non-SK receipt */}
        <button
          onClick={onNonSkReceipt}
          style={actionButtonStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#667eea';
            e.currentTarget.style.background = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.background = 'white';
          }}
        >
          <span style={{ fontSize: '20px' }}>🌍</span>
          <span>Nahrať neslovenský bloček</span>
        </button>
        </div>
      )}
    </div>
  );
};

export default QRScannerView;
